import { useEnvStore } from 'store'
import { RequestService } from '../RequestService'

export type GetContractHistoryKlineReq = {
  symbolId: string
  resolution: string | number
  from: number
  to: number
}

export enum TransferCoinType {
  Contract = 1,
  Coin = 2,
}

type Amount = number | string

type TransferCoinData = {
  itemId: ItemId
  amount: Amount
  type: TransferCoinType
  batchNo?: string
}

export enum ContractOrderType {
  /** 限价 */
  Limit = 1,
  /** 市价 */
  Market = 2,
  /** 止损 */
  StopLoss = 3,
  /** 止盈 */
  TakeProfit = 4,
  /** 市价止损 */
  MarketStopLoss = 5,
  /** 市价止盈 */
  MarketTakeProfit = 6,
  /** 双向止盈止损市价 */
  Both = 7,
}

export enum OrderPositionType {
  /** 双向 */
  Both = 0,
  /** 多仓 */
  Long = 1,
  /** 空仓 */
  Short = -1,
}

export enum OrderDirection {
  /** 卖 */
  Sell = -1,
  /** 买 */
  Buy = 1,
}

/** 仅减仓 */
export enum ReduceOnly {
  /** 是 */
  Yes = 1,
  /** 否 */
  No = -1,
}

export enum PendingOrderPreference {
  /** 更快成交 */
  Fast = 1,
  /** 更优价格 */
  Better = 2,
  /** 盘口排队 */
  Queue = 3,
}

export type OrderBuyParams = Partial<{
  isSim: boolean
  symbolId: string
  qty: string
  type: ContractOrderType
  price: string
  positionType: OrderPositionType
  direction: OrderDirection
  reduceOnly: ReduceOnly
  lossTriggerPrice: string
  profitTriggerPrice: string
  profitTriggerType: TriggerType
  lossTriggerType: TriggerType
  trustId: string
}>

export enum TriggerMode {
  /** 立即触发 */
  Immediate = 1,
  /** 价格触发 */
  Price = 2,
}

export enum TriggerType {
  /** 标记价格 */
  Mark = 1,
  /** 指数价格 */
  Index = 2,
  /** 最新价格 */
  Latest = 3,
}

export type IcebergPlaceOrderParams = Partial<{
  /** 是否模拟 */
  isSim: boolean
  /** 交易对id */
  symbolId: string
  /** 订单方向 */
  direction: OrderDirection
  /** 持仓方向 */
  positionType: OrderPositionType
  /** 单笔委托量 */
  singleQty: string
  /** 挂单笔数 */
  orderNum: string
  /** 挂单偏好 */
  preferenceType: PendingOrderPreference
  /** 触发模式 */
  triggerMode: TriggerMode
  /** 触发类型 */
  triggerType: TriggerType
  /** 触发价格 */
  triggerPrice: string
}>

class ContractRequest extends RequestService {
  constructor() {
    super()
  }

  protected setBaseUrl(): string {
    const envStore = useEnvStore()
    return envStore.config?.contract || ''
  }

  getDoubleContractAssetsAccount() {
    return this.post<ApiResponse.DoubleContractAssetsAccount>('/assets/account')
  }

  transferCoin(data: TransferCoinData) {
    return this.post('/assets/transfer', data)
  }

  getHistoryKline(data: GetContractHistoryKlineReq) {
    return this.post<SocketResponse.HistoryKlineResponse>('/pub/kline', data)
  }

  getExchangeInfo() {
    return this.post<ApiResponse.ContractExchangeInfo>('/pub/exchangeInfo')
  }

  /**
   * 止盈止损订单
   * @param data.isSim 是否模拟
   * @param data.symbolId 交易对id
   * @param data.qty 下单量
   * @param data.type 下单类型
   * @param data.price 价格 (市价不用添加)
   * @param data.positionType 持仓方向
   * @param data.direction 买卖方向
   * @param data.reduceOnly 仅减仓
   * @param data.lossTriggerPrice 止损触发价格 (type=7)
   * @param data.profitTriggerPrice 止盈触发价格(type=7)
   * @param data.trustId 下单器 止盈止损
   * @param data.profitTriggerType 止盈触发类型
   * @param data.lossTriggerType 止损触发类型
   * @returns
   */
  buyDoubleContractWithTakeProfitOrStopLoss(data: OrderBuyParams) {
    return this.post('/api/stopOrder', data)
  }

  /**
   * 下单
   * @param data
   * @returns
   */
  doubleContractOpenPosition(data: OrderBuyParams) {
    return this.post('/api/order', data)
  }

  icebergPlaceOrder(data: IcebergPlaceOrderParams) {
    return this.post('/iceberg/order', data)
  }

  /**
   * 更新杠杆
   * @param data.symbolId 交易对id
   * @param data.leverage 杠杆等级
   * @returns
   */
  updateLeverage(data: { symbolId: SymbolId; leverage: number }) {
    return this.post('/api/leverage', data)
  }

  /**
   * 获取限价单
   * @returns
   */
  getLimitOrders() {
    return this.post<ApiResponse.DoubleContractLimitOrder[]>('/api/getOpenOrders')
  }

  /**
   * 双向止盈止损部分仓位下单
   * @param data
   * @returns
   */
  doubleContractsStopPartOrder(data: OrderBuyParams) {
    return this.post('/api/stopPartOrder', data)
  }

  /**
   * 撤销订单
   * @param data.symbolId 交易对id
   * @param data.trustId 下单器
   * @returns
   */
  doubleContractCancelOrder(data: { symbolId: SymbolId; trustId: string }) {
    return this.post(`/api/cancel`, data)
  }

  /**
   * 冰山委托订单列表（策略）
   * @returns
   */
  getIcebergStrategyOrders(data: ApiRequest.PageBody) {
    return this.post<HasPageInfoResponse<ApiResponse.DoubleContractIcebergStrategyOrder>>(`/iceberg/currentStrategy`, data)
  }

  /**
   * 立即开始冰山委托
   * @param data.strategyId 策略id
   * @returns
   */
  doubleContractStartOrder(data: { strategyId: ApiResponse.DoubleContractIcebergStrategyOrder['strategyId'] }) {
    return this.post('/iceberg/startStrategy', data)
  }

  /**
   * 停止冰山委托
   * @param data.strategyId 策略id
   * @returns
   */
  doubleContractStopStrategy(data: { strategyId: ApiResponse.DoubleContractIcebergStrategyOrder['strategyId'] }) {
    return this.post('/iceberg/stopStrategy', data)
  }

  /**
   * 获取已平仓仓位列表（历史仓位）
   * @returns
   */
  getClosePositions(data: { positionId?: string; pageSize: number }) {
    return this.post<ApiResponse.ClosePosition[]>('/api/getClosePositions', data)
  }

  /**
   * 获取历史委托
   * @returns
   */
  getHistoricalCommission(data: { pageSize: number; positionId?: string }) {
    return this.post<ApiResponse.HistoricalCommission[]>('/api/getAllOrders', data)
  }

  /**
   * 获取历史成交
   * @returns
   */
  getHistoricalTrade(data: { pageSize: number; positionId?: string; trustId?: string; opType?: string; thId?: number }) {
    return this.post<ApiResponse.HistoricalTrade[]>('/api/getTradeHistory', data)
  }

  /**
   * 获取冰山委托成交记录
   * @param data.strategyId 策略ID
   * @param data.page 页码
   * @param data.pageSize 每页数量
   * @returns
   */
  getIcebergTradeHistory(data: { strategyId: number; page: number; pageSize: number }) {
    return this.post<ApiResponse.HistoricalTrade[]>('/iceberg/tradeHistory', data)
  }

  /**
   * 获取历史策略
   * @param data.page 页码
   * @param data.pageSize 每页条数
   * @returns
   */
  getHistoricalStrategy(data: ApiRequest.PageBody) {
    return this.post<HasPageInfoResponse<ApiResponse.HistoricalStrategy>>('/iceberg/historyStrategy', data)
  }

  /**
   * 获取资金流水
   * @param data.page 页码
   * @param data.pageSize 每页条数
   * @returns
   */
  getFundFlow(data: { ajId?: number; pageSize?: number; positionId?: string; symbolId?: SymbolId }) {
    return this.post<ApiResponse.FundFlow[]>('/assets/fundLog', data)
  }

  /**
   * 获取持仓中资金流水
   * @param data.symbolId 交易对id
   * @param data.ajId 资金流水id
   * @param data.positionId 持仓id
   * @returns
   */
  getFundFlowInPosition(data: { pageSize?: number; symbolId?: SymbolId; ajId?: number; positionId?: string }) {
    return this.post<ApiResponse.FundFlow[]>('/assets/positionFundLog', data)
  }

  /**
   * 更新保证金模式
   * @param data.symbolId 交易对id
   * @param data.marginType 保证金模式
   * @returns
   */
  doubleContractUpdateMarginType(data: { symbolId: SymbolId; marginType: number }) {
    return this.post('/api/marginType', data)
  }

  /**
   * 获取委托详情
   * @param data.trustId 委托id
   * @returns
   */
  doubleContractGetEntrustDetail(data: { trustId: string }) {
    return this.post<ApiResponse.HistoricalCommission[]>('/api/stopOrderRelation', data)
  }

  /** 调整保证金
   * @param data.symbolId 交易对id
   * @param data.amount 保证金数量
   * @param data.type 调整方向 1: 增加逐仓保证金，-1: 减少逐仓保证金
   * @param data.positionType 仓位模式：0 单向持仓；双仓位模式 1 做多仓位 ,-1 做空仓位
   * @returns
   */
  doubleContractUpdateMargin(data: { symbolId: SymbolId; amount: string; type: number; positionType: number }) {
    return this.post('/api/positionMargin', data)
  }

  /**
   * 开通合约
   * @param data
   * @returns
   */
  openContract(data = {}) {
    return this.post('/api/openFutures', data)
  }
}

export const ContractServices = new ContractRequest()
